import { gql, MutationResult, useMutation } from '@apollo/client';
import useFlags from 'launchdarkly-react-client-sdk/lib/useFlags';
import { useCallback } from 'react';
import { MAYA_GRAPHQL_ENDPOINT } from 'basics/constants/common.constants';
import { CustomerEngagementPlanning } from 'generated/maya.types';
import { cepFragment } from 'modules/maya/graphql/fragments/cep.fragments';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export const createCepMutationCall = gql`
mutation createCustomerEngagementPlanning($createCustomerEngagementPlanning: CustomerEngagementPlanningInput!, $sendToVeeva: Boolean!) {
  createCustomerEngagementPlanning(createCustomerEngagementPlanningInput: $createCustomerEngagementPlanning, sendToVeeva: $sendToVeeva) {
    ${cepFragment}
  }
}
`;

type ResultType = {
  createCustomerEngagementPlanning: CustomerEngagementPlanning;
};

type UseCreateCustomerEngagementPlanningMutation = [
  (
    customerEngagementPlanning: Partial<CustomerEngagementPlanning>,
    sendToVeeva?: boolean
  ) => Promise<CustomerEngagementPlanning | undefined | null>,
  MutationResult<ResultType>,
];

export const useCreateCepMutation = (): UseCreateCustomerEngagementPlanningMutation => {
  const [createCep, state] = useMutation<ResultType>(createCepMutationCall, {
    fetchPolicy: 'network-only',
    context: { clientName: MAYA_GRAPHQL_ENDPOINT },
  });
  const flags = useFlags();

  const executeMutation = useCallback(async (customerEngagementPlanning, sendToVeeva = true) => {
    try {
      const { data } = await createCep({
        variables: {
          createCustomerEngagementPlanning: customerEngagementPlanning,
          sendToVeeva: flags.mayaEnableVeevaBiogenlinc1816 && sendToVeeva,
        },
      });
      return data?.createCustomerEngagementPlanning;
    } catch (error: unknown) {
      if (error instanceof Error) {
        const customContext = extractApolloErrorDetails(error, customerEngagementPlanning);
        Logger.logError({
          error,
          customContext,
          transactionName: 'createCustomerEngagementPlanning.ts - useCreateCustomerEngagementPlanningMutation',
        });
      }
      return null;
    }
  }, [createCep, flags.mayaEnableVeevaBiogenlinc1816]);

  return [executeMutation, state];
};
